import React, { useEffect } from 'react';
import styles from './Nexus.module.scss';
import Container from '@components/ContentContainer';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectConversation,
  selectNewConversationID,
  selectNexusStatus,
} from '@reducers/data/nexus';
import ChatPrompt from './Prompt/Prompt';
import ChatConversation from './Conversation/Conversation';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ChatConversationHistory from './ConversationHistory/ConversationHistory';
import { ID } from '@app/types';
import { clearNewConversation, fetchNexusServiceStatus, fetchNexusStatus } from '@actions/nexus';
import { useOnVisible } from '@support/hooks/use-on-visible';
import { useAuthContext } from '@hooks/use-auth-context';
import analytics from '@support/analytics';
import { useReduxActionPolling } from '@support/hooks/use-redux-action-polling';

export default function Chat() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchUserData } = useAuthContext();

  const { conversationID } = useParams<{ conversationID: string }>();
  const nexusStatus = useSelector(selectNexusStatus);
  const conversation = useSelector(selectConversation(conversationID));
  const newChatID = useSelector(selectNewConversationID);
  const [conversationTargetContactID, setConversationTargetContactID] = React.useState<ID>(
    location.state?.contactID,
  );
  const [backGoesToPrevView, setBackGoesToPrevView] = React.useState<boolean>(false);
  const viewConversationID = conversationID || newChatID;

  useReduxActionPolling({
    selectShouldStartPolling: (state) => state.data.nexus.status.status === 'processing',
    pollAction: fetchNexusStatus,
  });

  const showBackButton = !!newChatID || !!conversationTargetContactID || conversation.isPersisted;
  const handleGoBack = () => {
    if (location.state?.fromHome) {
      dispatch(clearNewConversation);
      return navigate('/', { replace: true, state: {} });
    }

    if (conversationTargetContactID) {
      setConversationTargetContactID(null);
    }

    if (newChatID || conversation.isPersisted) {
      dispatch(clearNewConversation);
      navigate('/nexus', { replace: true, state: {} });
    }
  };

  useEffect(() => {
    if ((conversationID && conversation.id !== conversationID) || location.state?.contactID) {
      navigate('/nexus', { replace: true, state: {} });
    }
  }, []);

  useEffect(() => {
    // This isn't perfect, but is a decent default behavior. Ideal would be to
    // track if this should be set to false based on if the user did further state
    // changes to the Nexus view.
    if (location.state?.contactID) {
      setBackGoesToPrevView(true);
    }
  }, []);

  useEffect(
    () => () => {
      if (conversation.paywalled) {
        dispatch(clearNewConversation);
      }
    },
    [conversation],
  );

  useEffect(() => {
    dispatch(fetchNexusStatus());
    dispatch(fetchNexusServiceStatus());
    analytics.track('Nexus Opened');
  }, []);

  // Refresh user entitlements and nexus status
  useOnVisible(() => {
    fetchUserData();
    dispatch(fetchNexusStatus());
  }, []);

  return (
    <Container>
      <Container.Primary
        id="content"
        headerProps={{
          actions: false,
          title: 'Nexus',
          onGoBack: backGoesToPrevView ? undefined : handleGoBack,
          goBack: showBackButton,
        }}
      >
        <div className={styles.container}>
          <ChatConversation
            conversationID={viewConversationID}
            setConversationTargetContactID={setConversationTargetContactID}
          />
          <ChatPrompt
            conversationID={viewConversationID}
            conversationTargetContactID={conversationTargetContactID}
            nexusStatus={nexusStatus}
          />
        </div>
      </Container.Primary>
      <Container.Sidebar>
        <ChatConversationHistory setConversationTargetContactID={setConversationTargetContactID} />
      </Container.Sidebar>
    </Container>
  );
}
